import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Target, Users, Mail, Zap, Award } from 'lucide-react';

const FeatureCard = ({ icon, title, children, delay }) => (
    <motion.div 
        className="bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-2xl p-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: delay }}
    >
        <div className="inline-block p-3 bg-cyan-500/20 rounded-full mb-4">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-slate-100 mb-2">{title}</h3>
        <p className="text-slate-400">{children}</p>
    </motion.div>
);

const About = () => (
    <>
    <Helmet>
        <title>About RatingApp | Web3 Earn USDT by Rating Apps</title>
        <meta name="description" content="Learn about RatingApp — the decentralized Web3 platform that rewards users with USDT for rating top global apps. Our mission, how it works, and why to join." />
    </Helmet>
    <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4">
        <div className="max-w-2xl mx-auto py-12">
            
            <motion.div 
                className="text-center mb-16"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 mb-3">
                    About 
                </h1>
                <p className="text-slate-400 max-w-2xl mx-auto text-lg">
                    We are a decentralized, value-backed platform revolutionizing how users interact with and rate top global brands.
                </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
                 <motion.div 
                    className="bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl p-8"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                 >
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-cyan-400"><Target size={28} /> Our Mission</h2>
                    <p className="text-slate-300">
                        To empower users by rewarding them for their valuable insights on digital products. We bridge the gap between creators and consumers, ensuring a transparent, win-win ecosystem for all participants.
                    </p>
                </motion.div>
                 <motion.div 
                    className="bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl p-8"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-purple-400"><Award size={28} /> How It Works</h2>
                    <p className="text-slate-300">
                        Users subscribe to fixed staking packages, enabling them to rate applications in batches. For every rating completed, users earn a profit of up to 10%, creating a dynamic and rewarding experience.
                    </p>
                </motion.div>
            </div>

            <div className="text-center mb-16">
                 <h2 className="text-3xl font-bold mb-8">Why Choose Us?</h2>
                 <div className="grid md:grid-cols-3 gap-6">
                    <FeatureCard icon={<Zap size={24} className="text-cyan-300" />} title="Instant Rewards" delay={0.4}>
                        Earn rewards immediately after completing a rating batch. No waiting periods.
                    </FeatureCard>
                     <FeatureCard icon={<Users size={24} className="text-blue-300" />} title="Community Driven" delay={0.6}>
                        Join a vibrant community of users shaping the future of digital products.
                    </FeatureCard>
                     <FeatureCard icon={<Mail size={24} className="text-purple-300" />} title="Dedicated Support" delay={0.8}>
                        Have questions? Our team is ready to assist. Contact us at <a href="mailto:support@ratingapp.com" className="text-cyan-400 hover:underline">support@ratingapp.com</a>.
                    </FeatureCard>
                 </div>
            </div>

            <footer className="text-center text-sm text-slate-500 mt-12">
                &copy; {new Date().getFullYear()} RatingApp. All Rights Reserved.
            </footer>
        </div>
    </div>
    </>
);

export default About;
